/**
 * conductor-activity.tsx — "the Conductor is working, come back later".
 *
 * FOUNDER COMPLAINT, 2026-07-26, verbatim: "When the Conductor is thinking and
 * actually doing something, the interface must show it. Some icon, the usual
 * way an AI agent shows it is thinking — so the user understands it hasn't hung
 * or crashed, the process is running and I should just come back for the answer
 * later."
 *
 * WHAT IT REPLACES. The entire thinking-indicator surface was two things: a
 * static `<em className="opacity-60">…</em>` inside the empty bubble, and a
 * spinner on the Send button below the fold. Both are wrong in the same way —
 * the ellipsis DISAPPEARS the instant the first `text` event lands, and codex
 * narrates a preamble before every tool call, so within seconds of a
 * nine-minute turn the founder is looking at a finished-looking paragraph and
 * no sign anything is still running. He closed the drawer at ~30s and the
 * answer (9m03s, one real CTO card) was lost.
 *
 * WHY IT IS NOT SessionActivityDock. That dock is 392 lines built around a full
 * `LiveActivityState` plus tmux pane lines, none of which the Conductor can
 * supply — it has no card, no container, no pane. Refactoring a shipped
 * component for one new consumer is a worse trade than 110 local lines. What IS
 * reused is `formatTimer`, so the two surfaces cannot drift on `m:ss`.
 *
 * WHAT IT DELIBERATELY DOES NOT DO — the clock counts, it never judges.
 *   - No stall detection. A codex reasoning block at xhigh is legitimately
 *     silent for minutes; an amber "stalled" dot on a healthy turn re-teaches
 *     the exact false-crash reading this exists to fix.
 *   - No client-side timeout, no error state derived from elapsed time. The
 *     gateway's 900s heartbeat is the ONLY failure authority. A second one in
 *     the browser is how a working turn gets declared dead.
 */
import { Bot, Check, Wrench } from 'lucide-react';
import * as React from 'react';

import { formatTimer } from '../agent/live-activity-panel';

export type ConductorActivityKind = 'waiting' | 'thinking' | 'working' | 'done' | 'idle';

export interface ConductorActivityProps {
  kind: ConductorActivityKind;
  label: string;
  /** Epoch ms the turn STARTED. For a reload this is the assistant row's
   *  created_at, which openAssistantTurn inserts at send time — so the clock is
   *  right on a device that never saw the turn begin. */
  startedAtMs: number;
  toolCount: number;
  /** From `tool_result` with ok===false — an event the UI used to drop outright. */
  failedTools?: number;
  lastTool?: string;
  /** Tail of the most recent `thinking` delta. Never persisted, never appended
   *  to the turn content — this is the strongest "it is alive" signal on the
   *  wire and it was being dropped by a `default: return`. */
  reasoningTail?: string;
}

/**
 * Humanise a tool name for display.
 *
 * PROD EVIDENCE THIS IS NEEDED: chaos.conductor_messages seq 34's tool_calls
 * jsonb is literally
 *   ["/bin/bash -lc \"sed -n '1,240p' /tmp/ceo-codex-home-…/SKILL.md\""]
 * because claude-subprocess.ts falls back to `it.command`. Fixing it at the
 * source would leave every already-stored row garbage; fixing it here repairs
 * those too. The raw string is kept as the `title` so nothing is hidden.
 */
export function humanizeToolName(raw: string): string {
  const s = (raw ?? '').trim();
  if (!s) return 'Working';
  if (/^\/bin\/(ba)?sh\b/.test(s) || s.includes(' -lc ')) return 'Running a command';
  const chaos = /^chaos\.([a-z0-9_.]+)/i.exec(s);
  if (chaos) {
    const verb = CHAOS_TOOL_LABELS[chaos[1]!];
    if (verb) return verb;
    const words = chaos[1]!.replace(/[._]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return s.length > 48 ? `${s.slice(0, 47)}…` : s;
}

const CHAOS_TOOL_LABELS: Record<string, string> = {
  create_task: 'Creating a task',
  spawn_card: 'Creating a card',
  add_note: 'Adding a note',
  move_card: 'Moving a card',
  archive_card: 'Archiving a card',
  set_assignee: 'Assigning someone',
  invite_to_card: 'Inviting to a card',
  create_sub_task: 'Adding a sub-task',
  update_sub_task: 'Updating a sub-task',
  complete_sub_task: 'Completing a sub-task',
  comment_sub_task: 'Commenting on a sub-task',
  list_cards: 'Reading the board',
  get_card: 'Reading a card',
  list_roles: 'Reading the Workspace org chart',
  org_chart: 'Reading the Workspace org chart',
  org_map: 'Reading the Workspace org map',
  who_handles: 'Finding the right role',
  constitution: 'Reading the constitution',
  kb_search: 'Searching the knowledge base',
  kb_list_files: 'Browsing the knowledge base',
  read_card_files: 'Reading card files',
  skill_fetch: 'Fetching a skill',
  remember: 'Saving to memory',
  mention: 'Mentioning someone',
  notify: 'Sending a notification',
  ask_role: 'Asking a role',
  ask_human: 'Asking you',
  respond_to: 'Replying',
  escalate: 'Escalating',
  handoff: 'Handing off',
  schedule_self: 'Scheduling itself',
  search_credentials: 'Looking up credentials',
  api_request: 'Calling an API',
};

/**
 * The strip between the transcript and the composer — the structural slot
 * SessionActivityDock occupies in the session pane, and pinned above the fold,
 * which the old ellipsis-inside-the-last-bubble was not.
 */
export function ConductorActivity({
  kind,
  label,
  startedAtMs,
  toolCount,
  failedTools = 0,
  lastTool,
  reasoningTail,
}: ConductorActivityProps): React.ReactElement | null {
  const live = kind === 'waiting' || kind === 'thinking' || kind === 'working';
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!live) return;
    // 1s tick. The founder's whole complaint is that nothing MOVED; a clock
    // that advances is the single cheapest proof the process is alive.
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [live]);

  if (kind === 'idle') return null;

  const elapsed = startedAtMs > 0 ? Math.max(0, (live ? now : Math.max(now, startedAtMs)) - startedAtMs) : 0;
  const toolLabel = lastTool ? humanizeToolName(lastTool) : null;

  return (
    <div
      className="shrink-0 border-t border-border bg-muted/20 px-3 py-2 sm:px-4"
      data-testid="conductor-activity"
      data-kind={kind}
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-center gap-2 text-xs">
        {kind === 'done' ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        ) : (
          <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-foreground/40" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-foreground/70" />
          </span>
        )}
        <span className="shrink-0 font-medium text-foreground">{label}</span>
        <span className="shrink-0 text-muted-foreground" data-testid="conductor-activity-clock">
          · {formatTimer(elapsed)}
        </span>
        {toolCount > 0 && (
          <span className="shrink-0 text-muted-foreground">
            · {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
          </span>
        )}
        {failedTools > 0 && (
          <span className="shrink-0 text-amber-600" data-testid="conductor-activity-failed">
            · {failedTools} failed
          </span>
        )}
        {toolLabel && (
          <span
            className="inline-flex min-w-0 items-center gap-1 truncate text-muted-foreground"
            title={lastTool}
            data-testid="conductor-activity-tool"
          >
            <span className="shrink-0">·</span>
            <Wrench className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{toolLabel}</span>
          </span>
        )}
      </div>
      {live && reasoningTail && (
        <div
          className="mt-1 flex min-w-0 items-start gap-1.5 text-[11px] leading-snug text-muted-foreground/80"
          data-testid="conductor-activity-reasoning"
        >
          <Bot className="mt-[1px] h-3 w-3 shrink-0" aria-hidden />
          <span className="line-clamp-2 min-w-0 break-words">{reasoningTail}</span>
        </div>
      )}
      {live && (
        <div className="mt-1 text-[11px] text-muted-foreground/70">
          Still working — you can close this and come back; the answer is kept.
        </div>
      )}
    </div>
  );
}

/**
 * The Conductor's receipts: "Created" — cards this turn actually produced.
 *
 * Founder, 2026-07-26: "If it creates artefacts, cards or anything else, then
 * at the end there should be a block — like the one we did in the session —
 * with links to those artefacts or cards it created or launched."
 *
 * Visual language is borrowed from ArtifactsFooter in session-chat-pane, but it
 * is a SEPARATE component, not an extraction: that footer is populated by
 * cardFilesStorage.scanTouchedSince(), a filesystem scan of a card workspace.
 * The Conductor has no card workspace — claude-subprocess.ts rm's both temp
 * dirs at turn end. Same shape, different world.
 *
 * `Link`/navigation is supplied by the caller so this file stays free of router
 * context, which is what lets TurnRow render bare in copy-sites.test.tsx.
 */
export interface ConductorArtifact {
  kind?: 'card';
  cardId: string;
  boardId: string;
  orgId?: string;
  title?: string;
  columnKind?: string;
  priority?: string;
  orgName?: string;
  started?: boolean;
}

/** Title-case a column kind / priority for a chip. */
export function prettyToken(v: string | undefined): string | null {
  if (!v) return null;
  const s = v.replace(/[_-]+/g, ' ').trim();
  if (!s) return null;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function conductorArtifactSummary(a: ConductorArtifact): string[] {
  const parts: string[] = [];
  const col = prettyToken(a.columnKind);
  if (col) parts.push(col);
  const pri = prettyToken(a.priority);
  if (pri) parts.push(pri);
  if (a.started) parts.push('started');
  return parts;
}

export function conductorArtifactsSignature(
  artifacts: ConductorArtifact[] | undefined,
): string {
  if (!artifacts || artifacts.length === 0) return '';
  return artifacts.map((a) => `${a.cardId}:${a.started ? 1 : 0}:${a.title ?? ''}`).join('|');
}
